import { Button, Popover, PopoverContent, PopoverTrigger, RadioGroup, RadioGroupItem, Skeleton } from '@ibsys/kit';
import { EllipsisVerticalIcon } from 'lucide-react';
import { DialogsLayout, useSettings } from '@/entities/settings';
import { SkeletonAudio } from '@/features/skeletons';
import { cn } from '@/shared/lib';

export const DialogsSettings = () => {
  const { dialogsPage: dialogsSettings, setDialogsSettings } = useSettings();
  const layout = dialogsSettings.layout;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button icon={<EllipsisVerticalIcon />} variant={'ghost'} size={'sm'} />
      </PopoverTrigger>
      <PopoverContent key={layout}>
        <RadioGroup
          className={'flex flex-col gap-3.5'}
          value={layout}
          onChange={(next: DialogsLayout) => {
            setDialogsSettings({ layout: next });
          }}
        >
          <RadioGroupItem value={'one-page'}>
            <div
              className={
                'w-48 h-24 border-border border rounded-md hover:scale-105  transition-all flex p-1.5 items-center gap-1.5 overflow-hidden'
              }
            >
              <SkeletonTable className={'h-full w-1/3'} />
              <Skeleton className={'h-full w-1/3'} />
              <SkeletonDialog className={'h-full w-1/3'} />
            </div>
          </RadioGroupItem>
          <RadioGroupItem value={'classic'}>
            <div className={'flex gap-1.5 hover:scale-105 hover:translate-x-1 transition-all'}>
              <SkeletonTableLayout />
              <div
                className={cn('w-48 h-24 border-border border rounded-md p-1.5 items-center gap-1.5 overflow-hidden')}
              >
                <SkeletonMetaDialog className={'h-full'} />
              </div>
            </div>
          </RadioGroupItem>
          <RadioGroupItem value={'visualizer'}>
            <div className={'flex gap-1.5 hover:scale-105 hover:translate-x-1 transition-all'}>
              <SkeletonTableLayout />
              <div
                className={cn(
                  'w-48 h-24 border-border border rounded-md flex flex-col p-1.5 items-center gap-1.5 overflow-hidden',
                  '',
                )}
              >
                <SkeletonAudio className={'h-4'} />
                <SkeletonMetaDialog />
              </div>
            </div>
          </RadioGroupItem>
        </RadioGroup>
      </PopoverContent>
    </Popover>
  );
};

function SkeletonTableLayout() {
  return (
    <div className={'w-48 h-24 border-border border rounded-md flex p-1.5 items-center gap-1.5 overflow-hidden'}>
      <SkeletonTable />
    </div>
  );
}

function SkeletonTable({ className = '' }: { className?: string }) {
  return (
    <div className={cn('flex flex-col gap-1 h-full w-full', className)}>
      <Skeleton className={'h-3 w-full'} />
      <Skeleton className={'h-3 w-full'} />
      <Skeleton className={'h-3 w-full'} />
      <Skeleton className={'h-3 w-full'} />
      <Skeleton className={'h-3 w-full'} />
      <Skeleton className={'h-3 w-full'} />
    </div>
  );
}

function SkeletonDialog({ className = '' }: { className?: string }) {
  return (
    <div className={cn('flex flex-col gap-0.5 w-12 h-full overflow-hidden grow-0', className)}>
      {Array(10).fill(
        <>
          <Skeleton className={'w-7/10 h-2 shrink-0 bg-primary ml-auto '} />
          <Skeleton className={'w-9/10 h-3 shrink-0 mr-auto'} />
          <Skeleton className={'w-4/10 h-1 shrink-0 bg-primary ml-auto'} />
          <Skeleton className={'w-9/10 h-3 shrink-0 mr-auto'} />
        </>,
      )}
    </div>
  );
}

function SkeletonMetaDialog({ className = '' }: { className?: string }) {
  return (
    <div className={cn('flex gap-1 w-full flex-1 overflow-hidden', className)}>
      <Skeleton className={'h-full w-3/4 grow-0'} />
      <SkeletonDialog className={'h-full w-14'} />
    </div>
  );
}
